import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarIcon, Users, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const Booking = () => {
  const [searchParams] = useSearchParams();
  const roomTypeId = searchParams.get('roomTypeId');
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [roomType, setRoomType] = useState(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    if (roomTypeId && user) {
      loadRoomType();
    }
  }, [roomTypeId, user]);

  const loadRoomType = async () => {
    try {
      const data = await api.get(`/api/room-types/${roomTypeId}`);
      setRoomType(data);
    } catch (error) {
      toast.error('Erro ao carregar informações do quarto');
    }
  };

  const checkAvailability = async () => {
    if (!checkIn || !checkOut) {
      toast.error('Selecione as datas de check-in e check-out');
      return;
    }

    try {
      const data = await api.post('/api/check-availability', {
        room_type_id: roomTypeId,
        check_in: checkIn,
        check_out: checkOut,
      });
      
      if (data && data.length > 0) {
        setAvailability(data[0]);
        if (data[0].available) {
          const nights = Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24));
          setTotalPrice(roomType.base_price * nights);
          toast.success('Quarto disponível!');
        } else {
          toast.error('Quarto não disponível para essas datas');
          setTotalPrice(0);
        }
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao verificar disponibilidade');
    }
  };

  const handleBooking = async () => {
    if (!availability || !availability.available) {
      toast.error('Verifique a disponibilidade primeiro');
      return;
    }

    setLoading(true);
    try {
      const booking = await api.post(
        '/api/bookings',
        {
          room_type_id: roomTypeId,
          check_in: checkIn,
          check_out: checkOut,
          guests: guests,
        },
        token
      );

      const originUrl = window.location.origin;
      const checkoutResponse = await api.post(
        `/api/payments/create-checkout?booking_id=${booking.id}`,
        { origin_url: originUrl },
        token
      );

      window.location.href = checkoutResponse.url;
    } catch (error) {
      toast.error(error.message || 'Erro ao criar reserva');
      setLoading(false);
    }
  };

  if (!roomType) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="booking-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-display font-bold text-primary mb-8 text-center">
          Fazer Reserva
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="mb-6">
              <img
                src={roomType.images[0]}
                alt={roomType.name}
                className="w-full h-64 object-cover rounded-lg mb-4"
              />
              <h2 className="text-2xl font-display font-semibold text-primary mb-2">
                {roomType.name}
              </h2>
              <p className="text-text-secondary">{roomType.description}</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="checkIn">Check-in</Label>
                <Input
                  id="checkIn"
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="checkin-input"
                />
              </div>

              <div>
                <Label htmlFor="checkOut">Check-out</Label>
                <Input
                  id="checkOut"
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  min={checkIn || new Date().toISOString().split('T')[0]}
                  data-testid="checkout-input"
                />
              </div>

              <div>
                <Label htmlFor="guests">Número de Hóspedes</Label>
                <Input
                  id="guests"
                  type="number"
                  min="1"
                  max={roomType.max_guests}
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value))}
                  data-testid="guests-input"
                />
                <p className="text-xs text-text-muted mt-1">
                  Máximo: {roomType.max_guests} hóspedes
                </p>
              </div>

              <Button
                className="w-full bg-secondary hover:bg-secondary/90 text-primary rounded-full"
                onClick={checkAvailability}
                data-testid="check-availability-button"
              >
                <CalendarIcon className="h-4 w-4 mr-2" />
                Verificar Disponibilidade
              </Button>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-xl font-display font-semibold text-primary mb-6">
              Resumo da Reserva
            </h3>

            {availability && availability.available ? (
              <div className="space-y-4">
                <div className="border-b pb-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-text-secondary">Quarto:</span>
                    <span className="font-semibold">{roomType.name}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-text-secondary">Check-in:</span>
                    <span>{checkIn ? format(new Date(checkIn), 'dd/MM/yyyy') : '-'}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-text-secondary">Check-out:</span>
                    <span>{checkOut ? format(new Date(checkOut), 'dd/MM/yyyy') : '-'}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-text-secondary">Hóspedes:</span>
                    <span>{guests}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-text-secondary">Noites:</span>
                    <span>
                      {checkIn && checkOut
                        ? Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24))
                        : 0}
                    </span>
                  </div>
                </div>

                <div className="border-b pb-4">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total:</span>
                    <span className="text-accent">R$ {totalPrice.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-background p-4 rounded-lg text-sm text-text-secondary">
                  <p className="mb-2">✓ Cancelamento gratuito até 7 dias antes</p>
                  <p className="mb-2">✓ Pagamento seguro via Stripe</p>
                  <p>✓ Confirmação imediata por email</p>
                </div>

                <Button
                  className="w-full bg-accent hover:bg-accent/90 text-white rounded-full py-6 text-lg"
                  onClick={handleBooking}
                  disabled={loading}
                  data-testid="proceed-payment-button"
                >
                  {loading ? (
                    'Processando...'
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Prosseguir para Pagamento
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center py-12 text-text-secondary">
                Verifique a disponibilidade para ver o resumo
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
